import React from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components";
import { colors } from "../theme/colors";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  // Disable focus enforcement to prevent conflicts with modals
  disableAutoFocusItem: true,
  disableEnforceFocus: true,
  disableRestoreFocus: true,
};

const Picker = ({
  label,
  placeholder = "Select an option",
  data = [],
  value,
  onChange,
  multiple = false,
  searchable = false,
  cleanable = true,
  disabled = false,
  width = "100%",
  className,
  native = false,
  showCheckAll = false,
  ...props
}) => {
  // Helper to serialize values (handle arrays)
  const serializeValue = (val) => {
    if (Array.isArray(val)) {
      return JSON.stringify(val);
    }
    return val;
  };

  // Helper to deserialize values back to original format
  const deserializeValue = (val) => {
    if (
      typeof val === "string" &&
      (val.startsWith("[") || val.startsWith("{"))
    ) {
      try {
        return JSON.parse(val);
      } catch (e) {
        return val;
      }
    }
    return val;
  };

  // Check if data contains array values
  const hasArrayValues = data.length > 0 && Array.isArray(data[0]?.value);

  // Serialize data values for MUI Select
  const serializedData = hasArrayValues
    ? data.map((item) => ({
        ...item,
        value: serializeValue(item.value),
      }))
    : data;

  // Serialize the current value for comparison
  const serializedValue =
    hasArrayValues && value
      ? multiple
        ? (value || []).map(serializeValue)
        : serializeValue(value)
      : value;

  // Handle change event to match rsuite picker API
  const handleChange = (event) => {
    let newValue = event.target.value;

    // Deserialize back to original format if needed
    if (hasArrayValues) {
      if (multiple) {
        newValue = newValue.map(deserializeValue);
      } else {
        newValue = deserializeValue(newValue);
      }
    }

    if (onChange) {
      onChange(newValue);
    }
  };

  // Handle clear button click
  const handleClear = (event) => {
    event.stopPropagation();
    if (onChange) {
      onChange(multiple ? [] : null);
    }
  };

  // For multiple select, ensure value is always an array
  const selectValue = multiple
    ? serializedValue || []
    : (serializedValue ?? "");

  // Check if there's a value to show the clear button
  const hasValue = multiple
    ? selectValue.length > 0
    : selectValue !== "" && selectValue !== null;

  return (
    <StyledFormControl
      variant="outlined"
      disabled={disabled}
      fullWidth
      className={className}
      $width={width}
    >
      {label && <StyledInputLabel>{label}</StyledInputLabel>}
      <StyledSelect
        native={native}
        value={selectValue}
        onChange={handleChange}
        label={label}
        multiple={multiple}
        displayEmpty={!!placeholder}
        input={<OutlinedInput label={label} />}
        renderValue={
          multiple
            ? (selected) => {
                if (!selected || selected.length === 0) {
                  return <Placeholder>{placeholder}</Placeholder>;
                }
                // Display as comma-separated text (use serializedData for lookup)
                const labels = selected
                  .map((val) => {
                    const item = serializedData.find((d) => d.value === val);
                    if (item) {
                      return item.label;
                    }
                    // Fallback: if not found, try to deserialize and display nicely
                    if (typeof val === "string" && val.startsWith("[")) {
                      try {
                        const parsed = JSON.parse(val);
                        return Array.isArray(parsed) ? parsed[1] : val;
                      } catch (e) {
                        return val;
                      }
                    }
                    return val;
                  })
                  .join(", ");
                return <SelectedText>{labels}</SelectedText>;
              }
            : undefined
        }
        MenuProps={
          multiple
            ? MenuProps
            : {
                disableAutoFocusItem: true,
                disableEnforceFocus: true,
                disableRestoreFocus: true,
              }
        }
        endAdornment={
          cleanable && hasValue && !disabled ? (
            <InputAdornment position="end">
              <ClearButton
                aria-label="clear selection"
                onClick={handleClear}
                edge="end"
                size="small"
              >
                <CloseIcon fontSize="small" />
              </ClearButton>
            </InputAdornment>
          ) : null
        }
        {...props}
      >
        {/* Placeholder option for single select */}
        {!multiple && placeholder && (
          <MenuItem value="" disabled>
            <Placeholder>{placeholder}</Placeholder>
          </MenuItem>
        )}

        {/* Check All / Uncheck All for multi-select */}
        {multiple && showCheckAll && !native && [
          <CheckAllContainer key="__check-all__" onMouseDown={(e) => e.preventDefault()}>
            <CheckAllButton
              onClick={(e) => {
                e.stopPropagation();
                if (onChange) {
                  const allValues = hasArrayValues
                    ? serializedData.map((item) => deserializeValue(item.value))
                    : serializedData.map((item) => item.value);
                  onChange(allValues);
                }
              }}
            >
              Check All
            </CheckAllButton>
            <CheckAllButton
              onClick={(e) => {
                e.stopPropagation();
                if (onChange) {
                  onChange([]);
                }
              }}
            >
              Uncheck All
            </CheckAllButton>
          </CheckAllContainer>,
          <Divider key="__check-all-divider__" style={{ margin: '0' }} />,
        ]}

        {/* Native select uses option elements */}
        {native
          ? serializedData.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))
          : multiple
            ? // Multi-select with checkboxes
              serializedData.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  <Checkbox checked={selectValue.includes(item.value)} />
                  <ListItemText primary={item.label} />
                </MenuItem>
              ))
            : // Single select without checkboxes
              serializedData.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
      </StyledSelect>
    </StyledFormControl>
  );
};

const StyledFormControl = styled(FormControl)`
  width: ${(props) => props.$width};
  margin-bottom: 16px;

  .MuiInputLabel-root {
    color: ${colors.neutral[700]};
    font-weight: 500;
    font-size: 14px;

    &.Mui-focused {
      color: #2c5aa0;
    }

    &.Mui-disabled {
      color: ${colors.neutral[400]};
    }
  }
`;

const StyledInputLabel = styled(InputLabel)`
  && {
    color: ${colors.neutral[700]};
    font-weight: 500;
    font-size: 14px;
    background: white;
    padding: 0 4px;

    &.Mui-focused {
      color: #2c5aa0;
    }

    &.Mui-disabled {
      color: ${colors.neutral[400]};
    }
  }
`;

const ClearButton = styled(IconButton)`
  && {
    margin-right: 8px;
    padding: 4px;
    color: #878787;

    &:hover {
      color: #2c5aa0;
      background-color: rgba(44, 90, 160, 0.08);
    }
  }
`;

const StyledSelect = styled(Select)`
  && {
    border-radius: 20px;
    background: white;
    transition: all 0.3s ease;

    .MuiOutlinedInput-notchedOutline {
      border: 2px solid #e9ecef;
      border-radius: 20px;
    }

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: #2c5aa0;
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: #2c5aa0;
      border-width: 2px;
      box-shadow: 0 0 0 3px rgba(44, 90, 160, 0.1);
    }

    &.Mui-disabled {
      background: ${colors.neutral[100]};
      opacity: 0.6;

      .MuiOutlinedInput-notchedOutline {
        border-color: #e9ecef;
      }
    }

    .MuiSelect-select {
      padding: 12px 20px;
      min-height: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      box-sizing: border-box;
    }

    .MuiSelect-icon {
      color: #878787;
    }

    /* Checkbox styling */
    .MuiCheckbox-root {
      color: #878787;
      padding: 4px 8px 4px 4px;

      &.Mui-checked {
        color: #2c5aa0;
      }
    }

    /* Menu item styling for multi-select */
    .MuiMenuItem-root {
      padding: 8px 16px;

      &:hover {
        background-color: rgba(44, 90, 160, 0.08);
      }

      &.Mui-selected {
        background-color: rgba(44, 90, 160, 0.12);

        &:hover {
          background-color: rgba(44, 90, 160, 0.16);
        }
      }
    }

    /* ListItemText styling */
    .MuiListItemText-root {
      margin: 0;
    }
  }
`;

const Placeholder = styled.span`
  color: #999;
  font-size: 14px;
`;

const SelectedText = styled.span`
  color: #333;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

const CheckAllContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  background: ${colors.neutral[100]};
`;

const CheckAllButton = styled.button`
  background: none;
  border: none;
  color: ${colors.primary?.main || '#2c5aa0'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: rgba(44, 90, 160, 0.08);
  }
`;

export default Picker;
